/**
 * Contracts for the WhatsApp AI assistant customisation (owner decision 31 Oct 2026). Owner: wa-core.
 * Stored per tenant in wa_ai_assistant_settings; read/written through GET/PUT /v1/whatsapp-inbox/settings/assistant.
 * FAQs are not here: they live in the tenant knowledge base (FaqListSchema in ./knowledge, /v1/knowledge/faqs).
 *
 * The pure helpers below (business hours, handover matching, greeting / away text) run in the inbox engine before any
 * AI reply and are unit tested here, so the API and the portal agree on the same rules.
 */
import { z } from 'zod';

export const ASSISTANT_TONES = ['friendly', 'professional', 'concise'] as const;
export type AssistantTone = (typeof ASSISTANT_TONES)[number];
export const OUTSIDE_HOURS_MODES = ['away_only', 'away_and_ai'] as const;
export const DEFAULT_TIMEZONE = 'Asia/Dubai';
export const STYLE_NOTE_MAX = 500;
export const MESSAGE_TEXT_MAX = 1000;
export const MAX_HANDOVER_KEYWORDS = 50;
export const MAX_SCHEDULE_RANGES = 21;
/** The away message is sent at most once per conversation in this window. */
export const AWAY_REPEAT_MS = 12 * 3_600_000;

/** Days as in Date.getDay(): 0 = Sunday ... 6 = Saturday. */
export const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6] as const;

export function isValidTimezone(tz: string): boolean {
  try { new Intl.DateTimeFormat('en-US', { timeZone: tz }); return true; } catch { return false; }
}

const HHMM = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$|^24:00$/, 'Use HH:MM (00:00 to 24:00)');
const toMin = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));

/** One opening range. close < open is an overnight range: it ends the next day (e.g. Fri 22:00 to 02:00). */
export const HoursRangeSchema = z.object({ day: z.number().int().min(0).max(6), open: HHMM, close: HHMM }).strict()
  .refine((r) => r.open !== r.close, { message: 'Opening and closing time must differ', path: ['close'] })
  .refine((r) => r.open !== '24:00', { message: 'A range cannot open at 24:00', path: ['open'] });
export type HoursRange = z.infer<typeof HoursRangeSchema>;

/** Drop control characters except the line feed. */
const stripControl = (s: string) => [...s].filter((ch) => { const c = ch.charCodeAt(0); return c === 10 || (c >= 32 && c !== 127); }).join('');
/** Text the owner types: trimmed; control characters (except new lines) removed. */
const ownerText = (max: number) => z.string().max(max * 2).transform((s) => stripControl(s).trim()).pipe(z.string().max(max));

export const BusinessHoursSchema = z.object({
  enabled: z.boolean().default(false),
  timezone: z.string().min(1).max(64).default(DEFAULT_TIMEZONE).refine(isValidTimezone, { message: 'Unknown time zone' }),
  schedule: z.array(HoursRangeSchema).max(MAX_SCHEDULE_RANGES).default([]),
  /** outside hours: send only the away message, or send it and still let the AI answer */
  outsideHours: z.enum(OUTSIDE_HOURS_MODES).default('away_only'),
  awayMessage: ownerText(MESSAGE_TEXT_MAX).default(''),
  awayMessageAr: ownerText(MESSAGE_TEXT_MAX).default(''),
}).strict().superRefine((h, ctx) => {
  if (h.enabled && !h.schedule.length) ctx.addIssue({ code: 'custom', path: ['schedule'], message: 'Add at least one opening range' });
  if (h.enabled && !h.awayMessage && !h.awayMessageAr) ctx.addIssue({ code: 'custom', path: ['awayMessage'], message: 'Write the away message' });
});
export type BusinessHours = z.infer<typeof BusinessHoursSchema>;

export const GreetingSchema = z.object({
  enabled: z.boolean().default(false),
  text: ownerText(MESSAGE_TEXT_MAX).default(''),
  /** optional Arabic version, sent when the customer writes in Arabic */
  textAr: ownerText(MESSAGE_TEXT_MAX).default(''),
}).strict().superRefine((g, ctx) => {
  if (g.enabled && !g.text && !g.textAr) ctx.addIssue({ code: 'custom', path: ['text'], message: 'Write the greeting' });
});

export const HandoverSchema = z.object({
  /** topics or keywords (EN or AR, words or short phrases) that always go to a person */
  keywords: z.array(ownerText(60).pipe(z.string().min(1))).max(MAX_HANDOVER_KEYWORDS).default([])
    .transform((ks) => [...new Map(ks.map((k) => [k.toLowerCase(), k])).values()]),
  /** hand over whenever the customer asks for a person (default on) */
  onHumanRequest: z.boolean().default(true),
}).strict();

export const AssistantSettingsSchema = z.object({
  name: ownerText(60).default(''),
  tone: z.enum(ASSISTANT_TONES).default('friendly'),
  /** free text from the owner; untrusted: the prompt puts it in a delimited section that cannot override the rules */
  styleNote: ownerText(STYLE_NOTE_MAX).default(''),
  greeting: GreetingSchema.default({ enabled: false, text: '', textAr: '' }),
  businessHours: BusinessHoursSchema.default({ enabled: false, timezone: DEFAULT_TIMEZONE, schedule: [], outsideHours: 'away_only', awayMessage: '', awayMessageAr: '' }),
  handover: HandoverSchema.default({ keywords: [], onHumanRequest: true }),
  /** ground replies on the tenant knowledge base (FAQs + indexed sources) */
  useKnowledgeBase: z.boolean().default(true),
}).strict();
export type AssistantSettings = z.output<typeof AssistantSettingsSchema>;
export type AssistantSettingsInput = z.input<typeof AssistantSettingsSchema>;
export const DEFAULT_ASSISTANT_SETTINGS: AssistantSettings = AssistantSettingsSchema.parse({});

// ---------------------------------------------------------------------------------------------------------
// Business hours
// ---------------------------------------------------------------------------------------------------------
const WD: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

/** Weekday (0 = Sunday) and minute of the day of `at` in time zone `tz`. */
export function localDayMinute(at: Date, tz: string): { day: number; minute: number } {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(at);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return { day: WD[get('weekday')] ?? 0, minute: (Number(get('hour')) % 24) * 60 + Number(get('minute')) };
}

/** True when `at` falls inside one of the opening ranges (overnight ranges continue into the next day). */
export function isWithinBusinessHours(hours: Pick<BusinessHours, 'timezone' | 'schedule'>, at: Date): boolean {
  const { day, minute } = localDayMinute(at, hours.timezone);
  return hours.schedule.some((r) => {
    const open = toMin(r.open); const close = toMin(r.close);
    if (open < close) return r.day === day && minute >= open && minute < close;
    // overnight: the evening part on r.day, the early part on the following day
    return (r.day === day && minute >= open) || ((r.day + 1) % 7 === day && minute < close);
  });
}

/** Outside hours only when the schedule is switched on and `at` is not in any range. */
export const isOutsideHours = (s: AssistantSettings, at: Date) => s.businessHours.enabled && !isWithinBusinessHours(s.businessHours, at);

// ---------------------------------------------------------------------------------------------------------
// Handover matching (EN + AR)
// ---------------------------------------------------------------------------------------------------------
/**
 * Built-in "the customer asks for a person" phrases, used when handover.onHumanRequest is on. These are request
 * phrases, not bare nouns: a lone "human" / "انسان" also appears in "human resources", "human hair", "شعر انسان".
 */
export const HUMAN_REQUEST_KEYWORDS = [
  'talk to a human', 'speak to a human', 'talk to human', 'speak to human', 'speak with a human', 'chat with a human',
  'human agent', 'real human', 'real person', 'live agent', 'live person', 'agent please', 'representative',
  'customer service', 'speak to someone', 'talk to someone', 'speak with someone', 'speak to a person', 'talk to a person',
  'موظف', 'موظف خدمة العملاء', 'خدمة العملاء', 'ممثل خدمة', 'شخص حقيقي', 'انسان حقيقي', 'كلمني شخص', 'كلمني احد',
  'اكلم احد', 'اكلم شخص', 'اكلم انسان', 'اكلم موظف', 'اتكلم مع انسان', 'اتكلم مع شخص', 'التحدث مع انسان',
  'التحدث مع شخص', 'التحدث مع موظف', 'أريد التحدث مع موظف',
] as const;

const AR_PREFIXES = ['وال', 'بال', 'فال', 'كال', 'لل', 'ال', 'و', 'ف', 'ب', 'ل', 'ك'];
const ARABIC = /[؀-ۿ]/;

/**
 * Lower case, NFKC, Arabic letter variants folded (alef forms, ya, ta marbuta, Persian ya ی and kaf ک), diacritics
 * and tatweel removed.
 */
export function normalizeForMatch(s: string): string {
  return s.normalize('NFKC').toLowerCase()
    .replace(/[ً-ٰٟـ]/g, '')
    .replace(/[أإآٱ]/g, 'ا').replace(/[ىی]/g, 'ي').replace(/ک/g, 'ك').replace(/ة/g, 'ه').replace(/ؤ/g, 'و').replace(/ئ/g, 'ي');
}
const words = (s: string) => normalizeForMatch(s).split(/[^\p{L}\p{N}]+/u).filter(Boolean);

/** Fewest letters an Arabic word may keep once a prefix is stripped: 2 would let "بلد" match "ولد" (both → "لد"). */
const AR_MIN_STEM = 3;

/** A word and its forms without one Arabic prefix (and, with, the ...) or an English plural ending. */
function forms(w: string): Set<string> {
  const out = new Set([w]);
  if (ARABIC.test(w)) { for (const p of AR_PREFIXES) if (w.startsWith(p) && w.length - p.length >= AR_MIN_STEM) out.add(w.slice(p.length)); }
  else { if (w.length > 3 && w.endsWith('es')) out.add(w.slice(0, -2)); if (w.length > 2 && w.endsWith('s')) out.add(w.slice(0, -1)); }
  return out;
}
const same = (a: string, b: string) => { const fb = forms(b); for (const f of forms(a)) if (fb.has(f)) return true; return false; };

/** The first keyword or phrase of `list` found in `text` as whole words (phrases as consecutive words), else null. */
export function findKeyword(text: string, list: readonly string[]): string | null {
  const t = words(text);
  for (const k of list) {
    const kw = words(k);
    if (!kw.length) continue;
    for (let i = 0; i + kw.length <= t.length; i++) if (kw.every((w, j) => same(t[i + j]!, w))) return k;
  }
  return null;
}

export type HandoverMatch = { rule: 'keyword' | 'human_request'; match: string; note: string };

/** Deterministic handover check that runs before any AI reply. */
export function matchHandover(text: string, s: AssistantSettings): HandoverMatch | null {
  const kw = findKeyword(text, s.handover.keywords);
  if (kw) return { rule: 'keyword', match: kw, note: `Handover rule matched "${kw}". A person should reply.` };
  if (s.handover.onHumanRequest) {
    const h = findKeyword(text, HUMAN_REQUEST_KEYWORDS);
    if (h) return { rule: 'human_request', match: h, note: 'The customer asked to speak to a person.' };
  }
  return null;
}

// ---------------------------------------------------------------------------------------------------------
// Fixed texts
// ---------------------------------------------------------------------------------------------------------
export const messageLanguage = (text: string): 'en' | 'ar' => (ARABIC.test(text) ? 'ar' : 'en');
const pickText = (en: string, ar: string, lang: 'en' | 'ar') => (lang === 'ar' ? ar || en : en || ar) || null;
/** Greeting for the first message of a new conversation (the Arabic version when the customer writes Arabic and one exists). */
export const greetingText = (s: AssistantSettings, lang: 'en' | 'ar') => (s.greeting.enabled ? pickText(s.greeting.text, s.greeting.textAr, lang) : null);
export const awayText = (s: AssistantSettings, lang: 'en' | 'ar') => pickText(s.businessHours.awayMessage, s.businessHours.awayMessageAr, lang);

/** API view of the stored settings. */
export interface AssistantSettingsView { settings: AssistantSettings; configured: boolean; version: number; updatedAt: string | null; updatedBy: string | null }
